import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export function Reveal({ children, delay = 0, className = "", as = "div" }) {
  const reduce = useReducedMotion();
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={reduce ? false : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </Comp>
  );
}

export function SectionHeader({ index, label, title, description, action, light }) {
  return (
    <div className="mb-12 grid gap-6 border-b hairline pb-8 lg:mb-16 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-3">
        <p className="eyebrow">{index && <span className="text-[var(--accent-2)]">{index} / </span>}{label}</p>
      </div>
      <div className="lg:col-span-9 lg:flex lg:items-end lg:justify-between lg:gap-10">
        <div>
          <h2 className={`display-lg whitespace-pre-line ${light ? "text-white" : ""}`}>{title}</h2>
          {description && <p className="mt-5 max-w-2xl text-base text-[var(--text-dim)] md:text-lg">{description}</p>}
        </div>
        {action && <div className="mt-6 shrink-0 lg:mt-0">{action}</div>}
      </div>
    </div>
  );
}

export function CtaLink({ to, children, variant = "lime", testId, external }) {
  const cls = variant === "lime" ? "btn-primary" : "btn-ghost";
  const inner = (<>{children}<ArrowUpRight className="h-4 w-4" strokeWidth={2.2} /></>);
  if (external) return <a href={to} className={cls} data-testid={testId} target="_blank" rel="noreferrer">{inner}</a>;
  return <Link to={to} className={cls} data-testid={testId}>{inner}</Link>;
}

export function PageIntro({ label, title, description }) {
  return (
    <section className="relative overflow-hidden border-b hairline pt-32 pb-16 sm:pt-40 sm:pb-24">
      <div className="glow-violet pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px]" />
      <div className="container-x relative">
        <p className="eyebrow rise">{label}</p>
        <h1 className="display-xl rise rise-1 mt-6 max-w-5xl whitespace-pre-line">{title}</h1>
        {description && <p className="rise rise-2 mt-8 max-w-2xl text-base text-[var(--text-dim)] md:text-lg">{description}</p>}
      </div>
    </section>
  );
}

export function LoadingBlock({ label = "Loading" }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" data-testid="loading-block">
      <p className="eyebrow flex items-center gap-3"><span className="pulse-dot h-2 w-2 rounded-full bg-[var(--accent-2)]" />{label}</p>
    </div>
  );
}
